import { FinancialAccountTopUpRequest, ErrorDetailsDto } from '@interfaces/models';
import { getCostCenter, makeTopUp } from '@api/cost-centers/cost-centers.service';
import { getTopUpData } from '@api/onboarding/onboarding.service';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCreditCards } from '@pages/cost-centers/hooks';
import { PayUTokenize } from '@shared/components/payu';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { z } from 'zod';

const topUpFormSchema = (t: TFunction, currency: string) =>
  z
    .object({
      amount: z.preprocess(
        (value) => (typeof value === 'string' ? value.replace(',', '.') : value),
        z
          .number({ coerce: true })
          .min(20, t('FORMS.MIN_AMOUNT', { limit: 20, currency }))
          .refine((value) => Number(value.toFixed(2)) === value, {
            message: t('FORMS.INVALID_CURRENCY_VALUE', { currency }),
          }),
      ),
      existingCard: z
        .object({
          paymentMethodId: z.string().min(1, { message: t('FORMS.REQUIRED') }),
        })
        .optional(),
      newCard: z
        .object({
          firstName: z
            .string()
            .trim()
            .min(1, { message: t('FORMS.REQUIRED') })
            .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) }),
          lastName: z
            .string()
            .trim()
            .min(1, { message: t('FORMS.REQUIRED') })
            .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) }),
          card: z.object({
            token: z.string().min(1, { message: t('FORMS.REQUIRED') }),
            mask: z.string().min(1, { message: t('FORMS.REQUIRED') }),
          }),
        })
        .optional(),
    })
    .refine((data) => data.existingCard || data.newCard, {
      message: t('FORMS.REQUIRED'),
      path: ['existingCard', 'newCard'],
    });

type TopUpFormSchema = z.infer<ReturnType<typeof topUpFormSchema>>;

export const useCostCenterAccountTopUpsDrawer = ({ costCenterId }: { costCenterId: string }) => {
  const payURef = useRef<PayUTokenize | undefined>(undefined);
  const { t } = useTranslation();
  const { currency } = useCurrentUser();

  const { data: costCenterData, isLoading: isCostCenterLoading } = useQuery({
    queryKey: ['cost-center', costCenterId],
    queryFn: () => getCostCenter(costCenterId),
  });

  const { cards, cardsLoading } = useCreditCards({ accountId: costCenterData?.technicalAccountId });

  const form = useForm<TopUpFormSchema>({
    resolver: zodResolver(topUpFormSchema(t, currency)),
    mode: 'onBlur',
    defaultValues: {
      amount: 0,
      existingCard: undefined,
      newCard: undefined,
    },
  });

  const { data: topUpData, isLoading: isTopUpDataLoading } = useQuery({
    queryKey: ['cost-center-account-top-ups-data'],
    queryFn: () => getTopUpData(),
  });

  const { mutate: saveTopUpMutation, isPending: isSaveTopUpPending } = useMutation({
    mutationFn: async (data: FinancialAccountTopUpRequest) => {
      if (costCenterData?.technicalAccountId) {
        return await makeTopUp({ data, accountId: costCenterData.technicalAccountId });
      }
      throw new Error('No account ID found');
    },
    onSuccess: ({ redirectUri }) => {
      form.reset();
      window.location.replace(redirectUri);
    },
    onError: ({ error }: { error: ErrorDetailsDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const handleSaveTopUp = useCallback(
    (data: TopUpFormSchema) => {
      saveTopUpMutation({
        costCenterId,
        newCard: data.newCard && {
          amount: data.amount,
          ...data.newCard,
        },
        existingCard: data.existingCard && {
          amount: data.amount,
          ...data.existingCard,
        },
      });
    },
    [saveTopUpMutation, costCenterId],
  );

  const onSubmit = useCallback(async () => {
    if (!!form.getValues('newCard')) {
      const card = await payURef.current?.tokenize('MULTI').catch(() => {
        return null;
      });
      if (!card) return;
      form.setValue('newCard.card', card);
    }

    form.handleSubmit(handleSaveTopUp)();
  }, [form, handleSaveTopUp, payURef]);

  const isLoading = isTopUpDataLoading || cardsLoading || isCostCenterLoading;

  return {
    isSaveTopUpPending,
    topUpData,
    isLoading,
    onSubmit,
    currency,
    payURef,
    cards,
    form,
    t,
  };
};
